import React, { useCallback, useEffect, useState } from 'react';

import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { appendSearchParams, deleteSearchParams, getQueryValue } from '@shared/helpers';
import { TBreadCrumb } from '@shared/ui/BreadCrumbs/model/index.type';
import { useGetFiles } from '@widgets/Files/hooks/useGetFiles';
import { useMoveObject } from '@widgets/Files/hooks/useMoveObject';
import { IFIleEntity } from '@widgets/Files/model/types/fileEntity.interface';
import { TFileId } from '@widgets/Files/model/types/fileId.type';
import { TFolderId } from '@widgets/Files/model/types/folderId.type';
import { TFilesModalMode } from '@widgets/Files/model/types/modalMode.type';

import { useAppSelector } from '../../store';
import { filesActions } from '../../store/slices/filesSlice/filesSlice';

export const useFiles = () => {
    const dispatch = useDispatch();
    const { needFetchFiles } = useAppSelector((state) => state.filesReducer);

    const [modalMode, setModalMode] = useState<TFilesModalMode>(null);
    const [currentFileId, setCurrentFileId] = useState<TFileId | null>(null);
    const [currentFileInfo, setCurrentFileInfo] = useState<IFIleEntity | null>(null);
    const [currentDraggedFolder, setChangeCurrentDraggedFolder] = useState(null);
    const onHideModal = useCallback(() => {
        setModalMode(null);
    }, []);
    const [currentFolderId, setCurrentFolderId] = useState<TFolderId | null>(
        Number(getQueryValue({ query: 'folderId' }))
    );
    const [currentFolderDropdownId, setCurrentFolderDropdownId] = useState<TFolderId | null>(null);
    const { parsedFilesData, isDataLoading, isFetching, breadCrumbsData } =
        useGetFiles(currentFolderId);
    const deletingFiles = useIsMutating({ mutationKey: ['deleteFile'], exact: true });
    const deletingFolders = useIsMutating({ mutationKey: ['deleteFolder'], exact: true });
    const isDownloading = useIsMutating({ mutationKey: ['downloadObject'], exact: true }) > 0;
    const isUploading =
        useIsMutating({
            mutationKey: ['uploadFiles'],
            exact: true,
        }) > 0;
    const isFetchingFiles =
        useIsFetching({
            queryKey: ['files'],
        }) > 0;
    const isDeleting = deletingFiles + deletingFolders > 0;
    const moveObjectMutation = useMoveObject();

    const onChangeModalMode = useCallback((mode: TFilesModalMode) => {
        setModalMode(mode);
    }, []);
    const onChangeCurrentFile = useCallback((fileId: TFileId, fileInfo: IFIleEntity) => {
        setCurrentFileInfo(fileInfo);
        setCurrentFileId(fileId);
    }, []);
    const onChangeCurrentFolder = useCallback((folderId: TFolderId) => {
        setCurrentFolderId(folderId);
    }, []);
    const onChangeCurrentFolderInDropdown = useCallback((folderId: TFolderId) => {
        setCurrentFolderDropdownId(folderId);
    }, []);
    const onChangeCurrentDraggedFolder = useCallback((folderId: TFolderId | null) => {
        setChangeCurrentDraggedFolder(folderId);
    }, []);

    const onBreadCrumbClick = (breadCrumbId: TBreadCrumb['id']) => {
        if (breadCrumbId === null) {
            setCurrentFolderId(null);
            deleteSearchParams('folderId');
            return;
        }
        setCurrentFolderId(breadCrumbId);
        deleteSearchParams('folderId');
        appendSearchParams('folderId', String(breadCrumbId));
    };
    const onBreadCrumbDrop = (
        e: React.DragEvent<HTMLDivElement>,
        breadCrumbId: TBreadCrumb['id'] | 'default'
    ) => {
        const objectId =
            e.dataTransfer.getData('data-file-id') || e.dataTransfer.getData('data-folder-id');
        const type = e.dataTransfer.getData('file-type') as 'folder' | 'file';
        const moveToId = breadCrumbId;
        if (currentFolderId === moveToId) return;
        moveObjectMutation.mutate({ objectId, type, moveToId });
    };

    const contextFilesValue = {
        modalMode,
        onHideModal,
        onBreadCrumbClick,
        onChangeModalMode,
        onChangeCurrentFile,
        onChangeCurrentFolder,
        onChangeCurrentFolderInDropdown,
        onChangeCurrentDraggedFolder,
        currentDraggedFolder,
        currentFileInfo,
        currentFileId,
        currentFolderId,
        currentFolderDropdownId,
    };

    useEffect(() => {
        if (needFetchFiles) {
            // dispatch(fetchFiles());
            dispatch(filesActions.changeNeedFiles(false));
        }
    }, [dispatch, needFetchFiles]);

    return {
        contextFilesValue,
        modalMode,
        onHideModal,
        parsedFilesData,
        breadCrumbsData,
        isDataLoading,
        isFetching,
        isUploading,
        isDownloading,
        isDeleting,
        isFetchingFiles,
        onBreadCrumbDrop,
    };
};
